import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import NavigationItem from './NavigationItem';
import SidebarToggle from './SidebarToggle';
import { NAVIGATION_ITEMS } from './navigationConfig';
import './Sidebar.css';

const STORAGE_KEY = 'smartstorage_sidebar_state';
const MOBILE_BREAKPOINT = 768;

interface SidebarProps {
  currentView: string;
  onNavigate: (viewId: string) => void;
  onCollapseChange?: (isCollapsed: boolean) => void;
}

const getDefaultState = (): boolean => {
  return window.innerWidth < MOBILE_BREAKPOINT;
};

const Sidebar: React.FC<SidebarProps> = ({ currentView, onNavigate, onCollapseChange }) => {
  const navigate = useNavigate();
  const [isCollapsed, setIsCollapsed] = useState(() => {
    // Initialize from localStorage or viewport width
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const { isCollapsed } = JSON.parse(saved);
        return typeof isCollapsed === 'boolean' ? isCollapsed : getDefaultState();
      }
    } catch (error) {
      console.error('Failed to read sidebar state:', error);
    }
    return getDefaultState();
  });

  useEffect(() => {
    // Notify parent of initial state
    if (onCollapseChange) {
      onCollapseChange(isCollapsed);
    }
  }, []);

  const handleToggle = () => {
    const newState = !isCollapsed;
    setIsCollapsed(newState);
    
    // Notify parent of state change
    if (onCollapseChange) {
      onCollapseChange(newState);
    }
    
    // Persist to localStorage
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        isCollapsed: newState,
        lastUpdated: Date.now()
      }));
    } catch (error) {
      console.error('Failed to save sidebar state:', error);
    }
  };

  const handleNavigationClick = (itemId: string) => {
    const item = NAVIGATION_ITEMS.find(i => i.id === itemId);
    
    // If the item has a path different from /dashboard, navigate to that route
    if (item && item.path !== '/dashboard') {
      navigate(item.path);
    } else {
      // Otherwise, use the internal view navigation
      onNavigate(itemId);
    }
  };

  return (
    <aside 
      className={`sidebar ${isCollapsed ? 'collapsed' : 'expanded'}`}
      aria-label="Main navigation"
    >
      <SidebarToggle isCollapsed={isCollapsed} onToggle={handleToggle} />
      
      <nav className="sidebar-nav">
        {NAVIGATION_ITEMS.map((item) => (
          <NavigationItem
            key={item.id}
            icon={item.icon}
            label={item.label}
            path={item.path}
            isActive={currentView === item.id}
            isCollapsed={isCollapsed}
            onClick={() => handleNavigationClick(item.id)}
          />
        ))}
      </nav>
    </aside>
  );
};

export default Sidebar;
